import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { typo } from '@/lib/typo'

interface ConfirmOptions {
  title: string
  body?: string
  confirmLabel?: string
  danger?: boolean
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

interface Pending extends ConfirmOptions {
  resolve: (ok: boolean) => void
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null)

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => setPending({ ...options, resolve }))
  }, [])

  function close(ok: boolean) {
    pending?.resolve(ok)
    setPending(null)
  }

  const value = useMemo(() => confirm, [confirm])

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pending && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Отмена"
            className="absolute inset-0 bg-scrim"
            onClick={() => close(false)}
          />
          <div
            role="alertdialog"
            aria-modal="true"
            className="relative max-h-full w-full max-w-sm overflow-y-auto rounded-md border border-hairline bg-surface-1 p-4 shadow-e3 sm:p-6"
          >
            <h2 className="text-title-2 font-semibold text-ink-1 [overflow-wrap:anywhere]">
              {typo(pending.title)}
            </h2>
            {pending.body && <p className="mt-2 text-body text-ink-2">{typo(pending.body)}</p>}
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={() => close(false)}>
                Отмена
              </Button>
              <Button
                variant={pending.danger ? 'destructive' : 'primary'}
                onClick={() => close(true)}
              >
                {pending.confirmLabel ?? 'Подтвердить'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (ctx === null) throw new Error('useConfirm must be used within ConfirmProvider')
  return ctx
}

/** The confirm of the app, or null outside a ConfirmProvider (a presentational component
 *  rendered on its own: a test, an isolated story). */
export function useConfirmOptional(): ConfirmFn | null {
  return useContext(ConfirmContext)
}
